import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Form, Row, Col, Spinner, Alert, Tabs, Tab, Button } from 'react-bootstrap';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';

const Leaderboard = () => {
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cohortFilter, setCohortFilter] = useState('');
    const [programFilter, setProgramFilter] = useState('');
    const [programs, setPrograms] = useState([]);
    const [cohorts, setCohorts] = useState([]);
    const [activeTab, setActiveTab] = useState('overall');
    const { currentUser, userProfile } = useAuth();
    const functions = getFunctions();

    useEffect(() => {
        loadProgramsAndCohorts();
    }, []);

    useEffect(() => {
        fetchLeaderboard();
    }, [cohortFilter, programFilter]);

    useEffect(() => {
        if (userProfile?.role === 'learner') {
            setProgramFilter('');
            setCohortFilter(userProfile.cohortId || '');
            setActiveTab('my-cohort');
        }
    }, [userProfile?.role, userProfile?.cohortId]);

    const loadProgramsAndCohorts = async () => {
        try {
            // Load programs
            const programsRef = collection(db, 'programs');
            const programsSnapshot = await getDocs(programsRef);
            const fetchedPrograms = programsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPrograms(fetchedPrograms);

            // Load cohorts
            const cohortsRef = collection(db, 'cohorts');
            const cohortsSnapshot = await getDocs(cohortsRef);
            const fetchedCohorts = cohortsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setCohorts(fetchedCohorts);
        } catch (err) {
            devError('Error loading programs and cohorts:', err);
        }
    };

    const getFilteredCohorts = () => {
        if (!programFilter) return cohorts;
        return cohorts.filter(c => c.programId === programFilter);
    };

    const fetchLeaderboard = async () => {
        try {
            setLoading(true);
            setError(null);

            const getLeaderboardFn = httpsCallable(functions, 'getLeaderboard');
            const result = await getLeaderboardFn({
                cohortId: cohortFilter || null,
                programId: programFilter || null,
                limit: 50
            });

            if (result.data.success) {
                setLeaderboardData(result.data.leaderboard);
            } else {
                setError('Failed to load leaderboard data');
            }
        } catch (err) {
            devError('Error fetching leaderboard:', err);
            setError('Error loading leaderboard. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'overall') {
            setProgramFilter('');
            setCohortFilter('');
        } else if (tab === 'my-program' && userProfile?.programId) {
            setProgramFilter(userProfile.programId);
            setCohortFilter('');
        } else if (tab === 'my-cohort' && userProfile?.cohortId) {
            setProgramFilter('');
            setCohortFilter(userProfile.cohortId);
        }
    };

    const clearFilters = () => {
        setProgramFilter('');
        setCohortFilter('');
        setActiveTab('overall');
    };

    const getRankBadge = (rank) => {
        if (rank === 1) return <Badge bg="warning" text="dark">🥇 1st</Badge>;
        if (rank === 2) return <Badge bg="secondary">🥈 2nd</Badge>;
        if (rank === 3) return <Badge bg="danger">🥉 3rd</Badge>;
        return <Badge bg="light" text="dark">#{rank}</Badge>;
    };

    const getProgramName = (programId) => {
        if (!programId) return 'All Programs';
        const program = programs.find(p => p.id === programId);
        return program?.name || program?.programName || 'Unknown Program';
    };

    const getCohortName = (cohortId) => {
        if (!cohortId) return 'All Cohorts';
        const cohort = cohorts.find(c => c.id === cohortId);
        return cohort?.name || cohort?.cohortName || cohortId;
    };

    return (
        <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white py-3">
                <Row className="align-items-center mb-2">
                    <Col>
                        <h5 className="mb-0">🏆 Learner Leaderboard</h5>
                    </Col>
                </Row>
                {userProfile?.role !== 'learner' && (
                    <>
                        {/* Quick Filter Tabs */}
                        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-3">
                            <Tab eventKey="overall" title="Overall">
                            </Tab>
                            {userProfile?.programId && (
                                <Tab eventKey="my-program" title="My Program">
                                </Tab>
                            )}
                            {userProfile?.cohortId && (
                                <Tab eventKey="my-cohort" title="My Cohort">
                                </Tab>
                            )}
                        </Tabs>
                        {/* Detailed Filters */}
                        <Row className="align-items-center">
                            <Col md={5}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">Filter by Program</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={programFilter}
                                        onChange={(e) => {
                                            setProgramFilter(e.target.value);
                                            setCohortFilter(''); // Reset cohort when program changes
                                            setActiveTab('overall');
                                        }}
                                    >
                                        <option value="">All Programs</option>
                                        {programs.map(prog => (
                                            <option key={prog.id} value={prog.id}>
                                                {prog.name || prog.programName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={5}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">Filter by Cohort</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={cohortFilter}
                                        onChange={(e) => {
                                            setCohortFilter(e.target.value);
                                            setActiveTab('overall');
                                        }}
                                    >
                                        <option value="">All Cohorts</option>
                                        {getFilteredCohorts().map(cohort => (
                                            <option key={cohort.id} value={cohort.id}>
                                                {cohort.name || cohort.cohortName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                                {(programFilter || cohortFilter) && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={clearFilters}
                                        className="w-100 mt-3"
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </>
                )}
                {/* Current Filter Display */}
                {(programFilter || cohortFilter) && (
                    <div className="mt-2">
                        <small className="text-muted">
                            Showing: {programFilter ? getProgramName(programFilter) : 'All Programs'}
                            {' '}/{' '}
                            {cohortFilter ? getCohortName(cohortFilter) : 'All Cohorts'}
                        </small>
                    </div>
                )}
            </Card.Header>
            <Card.Body className="p-0">
                {error && <Alert variant="danger" className="m-3">{error}</Alert>}

                {loading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">Loading rankings...</p>
                    </div>
                ) : (
                    <Table hover responsive className="mb-0 align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4">Rank</th>
                                <th>Learner</th>
                                <th>Cohort</th>
                                <th className="text-center">Attendance</th>
                                <th className="text-center">Documents</th>
                                <th className="text-end pe-4">Total Points</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaderboardData.length > 0 ? (
                                leaderboardData.map((user) => (
                                    <tr key={user.userId} className={currentUser?.uid === user.userId ? "table-primary" : ""}>
                                        <td className="ps-4">{getRankBadge(user.rank)}</td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {user.profileImage ? (
                                                    <img
                                                        src={user.profileImage}
                                                        alt={user.displayName}
                                                        className="rounded-circle me-2"
                                                        width="32"
                                                        height="32"
                                                    />
                                                ) : (
                                                    <div
                                                        className="rounded-circle bg-light d-flex align-items-center justify-content-center me-2"
                                                        style={{ width: '32px', height: '32px' }}
                                                    >
                                                        {user.displayName?.charAt(0) || 'U'}
                                                    </div>
                                                )}
                                                <span className={currentUser?.uid === user.userId ? "fw-bold" : ""}>
                                                    {user.displayName} {currentUser?.uid === user.userId && "(You)"}
                                                </span>
                                            </div>
                                        </td>
                                        <td>{getCohortName(user.cohortId)}</td>
                                        <td className="text-center">
                                            <Badge bg={user.metrics?.attendanceRate >= 80 ? 'success' : 'warning'}>
                                                {Math.round(user.metrics?.attendanceRate || 0)}%
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            <Badge bg="info">
                                                {user.metrics?.verifiedDocs || 0} / {user.metrics?.totalDocs || 0}
                                            </Badge>
                                        </td>
                                        <td className="text-end pe-4 fw-bold text-primary">
                                            {user.points} pts
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5 text-muted">
                                        No data available yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );
};

export default Leaderboard;
